/**
 * Centralized debug configuration for controlling visibility of debug UI elements.
 *
 * Each feature can be controlled via environment variables:
 * - NEXT_PUBLIC_DEBUG_SESSION_PANEL - Session debug panel (bottom-right corner)
 * - NEXT_PUBLIC_DEBUG_MAP_INFO - Map loading info, coordinates, and locations list
 * - NEXT_PUBLIC_DEBUG_SUBSCRIPTION_SYNC - Stripe subscription sync buttons
 * - NEXT_PUBLIC_DEBUG_CLUE_COORDINATES - Clue coordinate debug info
 * - NEXT_PUBLIC_DEBUG_API_ROUTES - Enable debug API routes
 *
 * Set to "true" to enable, any other value or unset to disable.
 * In development mode, these default to true unless explicitly disabled.
 */

/**
 * Check if we're in development mode
 */
export function isDevelopment(): boolean {
  return process.env.NODE_ENV === "development";
}

/**
 * Check if we're in production mode
 */
export function isProduction(): boolean {
  return process.env.NODE_ENV === "production";
}

/**
 * Parse a debug environment variable.
 * Returns true if explicitly set to "true", or if in development and not explicitly set to "false".
 */
function parseDebugEnv(envVar: string | undefined, defaultInDev: boolean = true): boolean {
  if (envVar === "true") return true;
  if (envVar === "false") return false;
  // Default to the specified value in development, false in production
  return isDevelopment() ? defaultInDev : false;
}

/**
 * Debug feature flags
 */
export const DEBUG_FLAGS = {
  /**
   * Show the session debug panel in the bottom-right corner.
   * Displays session status, user email, and cookies.
   */
  get showSessionPanel(): boolean {
    return parseDebugEnv(process.env.NEXT_PUBLIC_DEBUG_SESSION_PANEL);
  },

  /**
   * Show map debug information including loading status,
   * API key verification, and the locations list below the map.
   */
  get showMapDebugInfo(): boolean {
    return parseDebugEnv(process.env.NEXT_PUBLIC_DEBUG_MAP_INFO);
  },

  /**
   * Show Stripe subscription sync debug panel with
   * sync buttons and subscription state details.
   */
  get showSubscriptionSync(): boolean {
    return parseDebugEnv(process.env.NEXT_PUBLIC_DEBUG_SUBSCRIPTION_SYNC);
  },

  /**
   * Show clue coordinate debug information including
   * lat/lng values, radius, and API key status.
   */
  get showClueCoordinates(): boolean {
    return parseDebugEnv(process.env.NEXT_PUBLIC_DEBUG_CLUE_COORDINATES);
  },

  /**
   * Enable debug API routes (/api/debug/*).
   * These routes are always disabled in production regardless of this setting.
   */
  get enableDebugApiRoutes(): boolean {
    // Always disabled in production for security
    if (isProduction()) return false;
    return parseDebugEnv(process.env.NEXT_PUBLIC_DEBUG_API_ROUTES);
  },
} as const;

/**
 * Helper to check if any debug features are enabled
 */
export function hasAnyDebugFeatures(): boolean {
  return (
    DEBUG_FLAGS.showSessionPanel ||
    DEBUG_FLAGS.showMapDebugInfo ||
    DEBUG_FLAGS.showSubscriptionSync ||
    DEBUG_FLAGS.showClueCoordinates
  );
}

/**
 * Get a summary of all debug flags for logging
 */
export function getDebugFlagsSummary(): Record<string, boolean> {
  return {
    sessionPanel: DEBUG_FLAGS.showSessionPanel,
    mapDebugInfo: DEBUG_FLAGS.showMapDebugInfo,
    subscriptionSync: DEBUG_FLAGS.showSubscriptionSync,
    clueCoordinates: DEBUG_FLAGS.showClueCoordinates,
    debugApiRoutes: DEBUG_FLAGS.enableDebugApiRoutes,
    isDevelopment: isDevelopment(),
  };
}
